import { toIsoDate, todayIso, useI18n, type FormatSettings } from '@daghfal/core';

const MILLISECONDS_PER_DAY = 86_400_000;

/**
 * A trial's last day and how far off it is, both in the tenant's own timezone (C6). The API ends an extended trial at
 * midnight after its last day in the tenant's zone, so the last day is the day of the moment before, read in that
 * zone: in the console's own zone it could be another day.
 */
export function trialDay(trialEndsAt: string, timeZone: string, settings: FormatSettings): {
  /** `YYYY-MM-DD`. */
  endsOn: string;
  /** Zero on the last day itself, negative once it has passed. */
  daysLeft: number;
} {
  const zoned = { ...settings, timeZone };
  const endsOn = toIsoDate(new Date(Date.parse(trialEndsAt) - 1), zoned);
  const daysLeft = Math.round((Date.parse(endsOn) - Date.parse(todayIso(zoned))) / MILLISECONDS_PER_DAY);
  return { endsOn, daysLeft };
}

/** A trial's last day as the console shows it: the date, then the days left or that it is over. */
export function useTrialLabel(): (trialEndsAt: string, timeZone: string) => string {
  const { t, tn, formatDate, settings } = useI18n();
  return (trialEndsAt, timeZone) => {
    const { endsOn, daysLeft } = trialDay(trialEndsAt, timeZone, settings);
    const left = daysLeft < 0 ? t('tenant.trial.over') : tn('tenant.trial.left', daysLeft);
    return t('tenant.trial.label', { date: formatDate(endsOn), left });
  };
}
